import {Collapse, css, useTheme} from '@mui/material';
import {
  GetStylesFunction,
  LightComponentBox,
  LightIconButton,
  omitNullsy,
  useLocalStorage,
  TranslateUtils,
  ConfigObjectType,
} from '@wallaby-ui/core';
import {
  DEFAULT_PAGE_SIZE,
  FILTER_ACTIVE_ICON_COLORS,
  MaterialTableWrapper,
  getDefaultMTOptions,
  useFormatters,
  useTableData,
  LightDebouncedTextField,
  LightDateRangeFilter,
  LightSavedFiltersButton,
  LightSelectedFilters,
  LightOrganizationFilter2,
} from '@wallaby-ui/document';
import {isEqual} from 'lodash';
import React from 'react';
import {useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';

import {
  DEFAULT_DOC_ACTIVE_SORTING,
  DEFAULT_DOC_FILTERS,
  DEFAULT_DOC_SORTING,
  DEFAULT_DOCUMENT_LIST_PARAMS,
  useEncounterDocumentListColumns,
  useFilters,
} from './EncounterDocumentListHooks';
import styles from '../EncounterLayoutTabs.less';
import {AppConfig} from 'src/js/app_config';

type Props = {
  encounterId: string;
  encounterStart: Date;
  encounterEnd?: Date;
  documentId: string;
  config: ConfigObjectType;
  query: any;
  changeQuery: (newValue: any) => void;
};

export const EncounterDocumentList: React.FC<Props> = ({
  encounterId,
  documentId,
  encounterStart,
  encounterEnd,
  config,
  query,
  changeQuery,
}) => {
  const theme = useTheme();
  const inlineStyles = getStyles({theme});

  const selectedDocumentId = useParams().documentId;

  const {filters, changeFilters} = useFilters({
    encounterId,
    selectedDocumentId,
    query,
    changeQuery,
  });
  const docListParams = omitNullsy({
    page: filters.docPaging.page ?? 0,
    size: filters.docPaging.pageSize ?? DEFAULT_PAGE_SIZE,
    ...DEFAULT_DOCUMENT_LIST_PARAMS,
    documentName: filters.doc?.documentName ?? '',
    documentLastUpdatedFrom: filters.doc?.documentLastUpdatedFrom ?? '',
    documentLastUpdatedTo: filters.doc?.documentLastUpdatedTo ?? '',
    documentShortcut: filters.doc?.documentLastUpdatedFloatingShortcutName ?? '',
    documentOrganizationIds: filters.doc?.documentOrganizationIds ?? '',
  });

  const docParams = new URLSearchParams(docListParams);
  if (Array.isArray(filters.docSorting)) {
    filters.docSorting.forEach((sortField) => {
      docParams.append('sort', `${sortField.field},${sortField.direction}`);
    });
  } else {
    docParams.append('sort', `${filters.docSorting.field},${filters.docSorting.direction}`);
  }
  const {data, isLoading, totalCount, totalPages} = useTableData<any, any[]>(`${AppConfig.service.url.documents}${encounterId}`, docParams);

  const formatters = useFormatters();
  // const {t} = formatters;
  const intl = useIntl();
  const columns = useEncounterDocumentListColumns(formatters, filters.docActiveSorting, encounterId, encounterStart, encounterEnd);
  const hasSorting = !isEqual(filters.docActiveSorting, DEFAULT_DOC_ACTIVE_SORTING);
  const hasFilters = !isEqual(omitNullsy(filters.doc, true), omitNullsy(DEFAULT_DOC_FILTERS, true));
  const [defaultScroll, setDefaultScroll] = useLocalStorage(`/encounter/${encounterId}.docListScroll`, 0);
  return (
    <div data-name='encounter-documents-list' css={inlineStyles.contentWrapper}>
      <LightComponentBox isOverflow={false} style={{borderRadius: '0px 0px 16px 16px'}}>
        <div
          style={{
            flex: 0,
            width: '100%',
            justifyContent: 'flex-end',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '8px',
            minHeight: '34px',
          }}
        >
          <LightSelectedFilters
            align='flex-end'
            width='auto'
            buttonType='chip-filled'
            marginBottom='0px'
            hasFilters={hasFilters}
            hasSorting={hasSorting}
            hasXlsxExport={false}
            hasPdfExport={false}
            handleResetSorting={() =>
              changeFilters({
                docSorting: DEFAULT_DOC_SORTING,
                docActiveSorting: DEFAULT_DOC_ACTIVE_SORTING,
                docPaging: {
                  ...filters.docPaging,
                },
              })
            }
            handleResetFilters={() => {
              changeFilters({
                doc: DEFAULT_DOC_FILTERS,
                docPaging: {
                  ...filters.docPaging,
                  page: 0,
                },
              });
            }}
          />
          <LightIconButton
            iconName='filterIcon'
            useFixedViewBox='0 0 20 16'
            style={{width: 20, height: 16}}
            title={filters.docFiltersOpen ? 'layout.filters.closeFilters' : 'layout.filters.openFilters'}
            onClick={() =>
              changeFilters({
                docFiltersOpen: !filters.docFiltersOpen,
              })
            }
            selected={filters.docFiltersOpen}
            colors={hasFilters && !filters.docFiltersOpen ? FILTER_ACTIVE_ICON_COLORS : undefined}
          />
          <LightSavedFiltersButton
            storageIdPrefix={`/encounter/${encounterId}.documents`}
            component='encounterDocuments'
            config={config}
            filter={filters.doc}
            setFilter={(newFilter) =>
              changeFilters({
                doc: newFilter,
                docPaging: {
                  ...filters.docPaging,
                  page: 0,
                },
              })
            }
          />
        </div>
        <div
          style={{
            flex: 0,
            display: 'flex',
            marginBottom: '8px',
          }}
        >
          <Collapse in={filters.docFiltersOpen} timeout={500} style={{minHeight: 'none', width: '100%'}}>
            <div css={inlineStyles.filteringContainerWrapper}>
              <div style={{width: 'calc(50% - 5px)'}}>
                <LightDateRangeFilter
                  placement='left-start'
                  config={{
                    name: 'documentLastUpdated',
                    ui: {label: 'filter.document.date'},
                  }}
                  value={
                    filters.doc.documentLastUpdatedFrom && filters.doc.documentLastUpdatedTo
                      ? {
                          startDate: filters.doc.documentLastUpdatedFrom,
                          endDate: filters.doc.documentLastUpdatedTo,
                          shortcut: filters.doc.documentLastUpdatedFloatingShortcutName,
                        }
                      : null
                  }
                  onChange={(newValue) => {
                    changeFilters({
                      doc: {
                        ...filters.doc,
                        documentLastUpdatedFrom: newValue?.startDate,
                        documentLastUpdatedTo: newValue?.endDate,
                        documentLastUpdatedFloatingShortcutName: newValue?.shortcut,
                      },
                      docPaging: {
                        ...filters.docPaging,
                        page: 0,
                      },
                    });
                  }}
                  dateOnly
                  fullWidth
                  showShortcuts
                />
              </div>
              <div style={{width: 'calc(50% - 5px)'}}>
                <LightOrganizationFilter2
                  appConfig={config}
                  config={{
                    name: 'documentOrganizationIds',
                    ui: {label: 'filter.document.department'},
                  }}
                  multi
                  value={filters.doc.documentOrganizationIds}
                  onChange={(newOrganizationIds) => {
                    changeFilters({
                      doc: {
                        ...filters.doc,
                        documentOrganizationIds: newOrganizationIds,
                      },
                      docPaging: {
                        ...filters.docPaging,
                        page: 0,
                      },
                    });
                  }}
                />
              </div>
              <LightDebouncedTextField
                style={{
                  width: '100%',
                }}
                label={TranslateUtils.translate('filter.document.name', intl)}
                name='documentName'
                value={filters.doc.documentName ?? ''}
                fullWidth
                onChange={(newValue) =>
                  changeFilters({
                    doc: {
                      ...filters.doc,
                      documentName: newValue,
                    },
                    docPaging: {
                      ...filters.docPaging,
                      page: 0,
                    },
                  })
                }
                hidden={!filters.docFiltersOpen}
              />
            </div>
          </Collapse>
        </div>

        <MaterialTableWrapper
          getRowClass={(rowData, index) => {
            if (selectedDocumentId && rowData?.documentVersionId === +selectedDocumentId) {
              return styles.dlEncounterDocumentSelectedRow;
            }
            return index % 2 === 0 ? 'ag-row-even' : 'ag-row-odd';
          }}
          options={{
            ...getDefaultMTOptions(DEFAULT_PAGE_SIZE),
          }}
          paginationOptions={{
            showPageSize: false,
            showResultCount: false,
            showMoveToPage: false,
            maxPageClickers: 2,
          }}
          variant='big'
          useMaxHeight
          columns={columns}
          data={data ?? []}
          isLoading={isLoading}
          totalCount={totalCount}
          page={filters.docPaging.page ?? 0}
          totalPages={totalPages}
          onChangePage={(page, pageSize) => {
            if (!isLoading) {
              changeFilters({
                docPaging: {
                  ...filters.docPaging,
                  page,
                  pageSize,
                },
              });
            }
          }}
          /* onChangeRowsPerPage={(pageSize) => { // NOTE: MaterialTable is dumb and calls this on initial load
            if (!isLoading) {
              changeFilters({
                docPaging: {
                  ...filters.docPaging,
                  pageSize,
                  page: clampPageNumber(filters.docPaging.page, pageSize, totalCount),
                },
              });
            }
          }} */
          controlledPageSize={filters.docPaging.pageSize ?? DEFAULT_PAGE_SIZE}
          onOrderChange={(colIndex, direction) => {
            const columnId = columns[colIndex].field as string;
            if (!isLoading) {
              const isServerSideSort = columnId === 'EFFECTIVE_AT';
              changeFilters({
                ...(isServerSideSort
                  ? {
                      docSorting: [
                        {field: 'EFFECTIVE_AT', direction},
                        {field: 'VERSION', direction},
                      ],
                    }
                  : {}),
                docActiveSorting: {field: columnId, direction},
                docPaging: {
                  ...filters.docPaging,
                },
              });
            }
          }}
          controlledSort={filters.docActiveSorting}
          direction={filters.docActiveSorting.direction}
          defaultScroll={{to: defaultScroll, dependOn: `${defaultScroll},${documentId}`}}
          onScroll={(event) => {
            setDefaultScroll((event.target as HTMLDivElement).scrollTop);
          }}
        />
      </LightComponentBox>
    </div>
  );
};

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    width: 100%;
    height: 100%;
  `,
  filteringContainerWrapper: css`
    width: 100%;
    display: flex;
    column-gap: 10px;
    justify-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;
  `,
  filterClearButton: css`
    background: #d8e8ff;
    border-radius: 40px;
    color: #154fa6;
    font-weight: bold;
  `,
});
